/**
 * Single registry for external data / documentation URLs (no scattered string literals).
 * Live fetches build request URLs from these constants + lat/lng query params.
 */

/** Open-Meteo Marine Weather Forecast API (waves, swell, SST, currents). */
export const OPEN_METEO_MARINE_API = "https://marine-api.open-meteo.com/v1/marine";
export const OPEN_METEO_FORECAST_API = "https://api.open-meteo.com/v1/forecast";
export const OPEN_METEO_MARINE_DOCS = "https://open-meteo.com/en/docs/marine-weather-api";

export const CANADA_CKAN_API_BASE = "https://open.canada.ca/data/api/3/action";
export const CANADA_OPEN_SEARCH_PORTAL = "https://search.open.canada.ca/opendata/?owner_org=dfo-mpo";
export const DFO_STATS_PORTAL = "https://www.dfo-mpo.gc.ca/stats/commercial/index-eng.htm";

export const USGS_NLDI_API = "https://api.water.usgs.gov/nldi/positions";
export const USGS_NHD_PORTAL = "https://www.usgs.gov/national-hydrography";

export const EPA_DATA_PORTAL = "https://www.epa.gov/data";
export const EPA_WATERS_OPENAPI = "https://watersgeo.epa.gov/openapi/waters/";
export const EPA_WATERDATA_DOWNLOADS = "https://www.epa.gov/waterdata/waters-geospatial-data-downloads";
export const EPA_STORET = "https://www.epa.gov/waterdata/water-quality-data";

export const NANOOS_ERDDAP = "https://erddap.nanoos.org/erddap/index.html";
export const NANOOS_PORTAL = "https://www.nanoos.org/";

export const OBIS_API_BASE = "https://api.obis.org";
export const OBIS_PORTAL = "https://obis.org/";
export const OBIS_API_DOCS = "https://obis.org/manual/api/";

export const NOAA_COASTWATCH_ERDDAP = "https://coastwatch.pfeg.noaa.gov/erddap/index.html";
export const NOAA_CO_OPS = "https://tidesandcurrents.noaa.gov/";

export const CCME_CWQG_TEMPERATURE = "https://ccme.ca/en/res/waterquality.html";

export const OCEANIC_DC_GOVERNANCE_HUB =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_OCEANIC_DC_GOVERNANCE_SITE_URL) ||
  "https://oceanic-datacenters-governance.netlify.app/";

/** Feeds shown on the dedicated Sources page — count for footer link copy. */
export const DATA_FEED_ENDPOINTS = [
  {
    name: "NOAA CoastWatch (ERDDAP)",
    dataset: "MUR SST and regional grids",
    status: "LIVE",
    url: NOAA_COASTWATCH_ERDDAP,
    apiDocUrl: NOAA_COASTWATCH_ERDDAP,
  },
  {
    name: "NANOOS ERDDAP",
    dataset: "Pacific Northwest ocean observing",
    status: "LIVE",
    url: NANOOS_ERDDAP,
    apiDocUrl: NANOOS_PORTAL,
  },
  {
    name: "Open-Meteo Marine",
    dataset: "Global marine current + hourly SST",
    status: "LIVE",
    url: OPEN_METEO_MARINE_DOCS,
    apiDocUrl: OPEN_METEO_MARINE_API,
  },
  {
    name: "Open-Meteo Forecast",
    dataset: "Atmosphere at coordinates",
    status: "LIVE",
    url: "https://open-meteo.com/en/docs",
    apiDocUrl: OPEN_METEO_FORECAST_API,
  },
  {
    name: "Canada Open Data (CKAN)",
    dataset: "DFO packages (package_search)",
    status: "LIVE",
    url: CANADA_OPEN_SEARCH_PORTAL,
    apiDocUrl: `${CANADA_CKAN_API_BASE}/package_search`,
  },
  {
    name: "USGS NLDI",
    dataset: "Hydrography-linked features at point",
    status: "LIVE",
    url: USGS_NHD_PORTAL,
    apiDocUrl: USGS_NLDI_API,
  },
  {
    name: "OBIS",
    dataset: "Marine species occurrences (API v3)",
    status: "LIVE",
    url: OBIS_PORTAL,
    apiDocUrl: OBIS_API_DOCS,
  },
  {
    name: "US EPA — Data & STORET context",
    dataset: "Water program portals (live layers via WATERS / Water Data)",
    status: "LIVE",
    url: EPA_DATA_PORTAL,
    apiDocUrl: EPA_STORET,
  },
  {
    name: "NOAA CO-OPS / WCOFS",
    dataset: "West Coast forecast fields",
    status: "LIVE",
    url: "https://opendap.co-ops.nos.noaa.gov/",
    apiDocUrl: "https://tidesandcurrents.noaa.gov/",
  },
  {
    name: "Columbia River DART",
    dataset: "Fish passage and temperature",
    status: "LIVE",
    url: "https://www.cbr.washington.edu/dart/",
    apiDocUrl: "https://www.cbr.washington.edu/dart/",
  },
  {
    name: "DFO SCIP (Pacific Climate)",
    dataset: "Salmon climate indicators",
    status: "CONNECTED",
    url: "https://services.pacificclimate.org/scip/",
    apiDocUrl: "https://services.pacificclimate.org/scip/",
  },
];

export const DATA_FEED_API_COUNT = DATA_FEED_ENDPOINTS.length;

/** Shown in geodata “references” strip — same links backend uses conceptually. */
export const VERIFICATION_REFERENCE_LINKS = [
  { name: "Open Government (Canada) — DFO", url: CANADA_OPEN_SEARCH_PORTAL },
  { name: "US EPA — Data", url: EPA_DATA_PORTAL },
  { name: "EPA WATERS Geo (OpenAPI)", url: EPA_WATERS_OPENAPI },
  { name: "USGS — National Hydrography", url: USGS_NHD_PORTAL },
  { name: "USGS — Watershed Boundary Dataset", url: "https://www.usgs.gov/national-hydrography/watershed-boundary-dataset" },
  { name: "USGS — 3D Elevation Program", url: "https://www.usgs.gov/3d-elevation-program" },
  { name: "Open-Meteo", url: "https://open-meteo.com/" },
  { name: "OBIS API", url: OBIS_API_DOCS },
  { name: "NANOOS", url: NANOOS_PORTAL },
];
